import React, { useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

import useGlobalStyles from '../../../styles';
import useStyles from '../../../styles/tags';
import { setSensorsById } from '../../../state/actions';
import { useAppDispatch, useAppSelector } from '../../../state/store';
import { getCurrentLocation, getSensorsById } from '../../../state/selectors';
import { themeProps } from '../../../styles/theme';
import { SpaceParams } from '../../Map/mapHelpers';
import { updateSensorProperties } from '../../../services/apiService';

interface TagNameFormsProps {
  selectedTag: string;
  setSelectedTag: (value: string) => void;
  existingTaggedSensors: string[];
  allSpaces: SpaceParams[] | undefined;
}

function TagNameForms({
  selectedTag,
  setSelectedTag,
  existingTaggedSensors,
  allSpaces,
}: TagNameFormsProps) {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const { classes: globalClasses } = useGlobalStyles();
  const theme = useTheme();
  const currentLocation = useAppSelector(getCurrentLocation);
  const sensorsById = useAppSelector(getSensorsById);
  const [isEditingTagName, setIsEditingTagName] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState('');

  useEffect(() => setIsEditingTagName(false), [currentLocation]);

  const handleNameChange = async () => {
    if (!isEditingTagName) {
      setIsEditingTagName(true);
      return;
    }

    const trimmedName = newTagName.trim();

    if (!trimmedName) {
      setNameError('Tag name cannot be empty');
      return;
    }

    const isDuplicate =
      trimmedName !== selectedTag && allSpaces?.some((space) => space.title === trimmedName);

    if (isDuplicate) {
      setNameError('A tag with this name already exists');
      return;
    }

    setNameError('');
    setIsSaving(true);

    try {
      const results = await Promise.all(
        existingTaggedSensors.map(async (sensorId) => {
          const sensor = sensorsById.get(sensorId) ?? { id: sensorId };
          const allTags = sensor.tags || [];

          // Remove old group
          const filteredTags = allTags.filter((tag) => tag !== selectedTag);

          // Add new group if not present
          const newTags = [...filteredTags, trimmedName];

          try {
            const response = await updateSensorProperties(sensor.id, { tags: newTags });
            if (response.id) {
              const updatedSensorDetails = { ...sensor, ...response };
              dispatch(setSensorsById([updatedSensorDetails]));
            }
            return true;
          } catch (error) {
            enqueueSnackbar(`Failed to update tag for sensor ${sensor.id}, ${error}`, {
              variant: 'error',
              persist: true,
            });
            return false;
          }
        })
      );

      const failedCount = results.filter((success) => !success).length;

      if (failedCount > 0) {
        enqueueSnackbar(
          `Tag updated, but ${failedCount} of ${results.length} sensor(s) failed to update`,
          { variant: 'warning' }
        );
      } else {
        enqueueSnackbar('Tag name updated successfully', { variant: 'success' });
      }

      setSelectedTag(trimmedName);
      setNewTagName('');
      setIsEditingTagName(false);
    } finally {
      setIsSaving(false);
    }
  };

  // check whether Tag exists
  const tagExists = !!allSpaces?.some((space) => space.title === selectedTag);

  return (
    <Box sx={{ marginTop: '25px' }}>
      {isEditingTagName && (
        <>
          <Box className={globalClasses.spaceBetweenFlex}>
            <TextField
              placeholder="Tag name"
              value={newTagName}
              onChange={(e) => {
                setNewTagName(e.target.value);
                if (nameError) setNameError('');
              }}
              variant="standard"
              sx={{ width: '90%', padding: '0 10px 0 0' }}
              error={!!nameError}
              helperText={nameError || undefined}
              disabled={isSaving}
            />
            <IconButton
              aria-label="Confirm tag name"
              onClick={handleNameChange}
              disabled={isSaving}
              size="small"
            >
              <CheckCircleIcon color="success" />
            </IconButton>
            <IconButton
              aria-label="Cancel tag name edit"
              onClick={() => {
                setIsEditingTagName(false);
                setNewTagName('');
                setNameError('');
              }}
              disabled={isSaving}
              size="small"
            >
              <CancelIcon color="error" />
            </IconButton>
            <br />
          </Box>
          <span style={{ fontSize: themeProps.textSize.small }}>
            [Info]: Updating name fields will update tag for shown sensors only
          </span>
        </>
      )}

      {!isEditingTagName && (
        <Box className={globalClasses.spaceBetweenFlex}>
          <Autocomplete
            disablePortal
            options={allSpaces?.map((item) => item.title) ?? []}
            sx={{ width: `${tagExists ? '90%' : '100%'}` }}
            value={selectedTag}
            onChange={(event, newValue) => {
              const isNewTagSuggestion = newValue?.startsWith('Create new ');
              // Extract the actual value if it's a "Create new" option
              const actualValue = isNewTagSuggestion
                ? newValue?.replace(/^Create new "|"$/g, '')
                : newValue;

              setSelectedTag(actualValue ?? '');
            }}
            renderOption={(props, option) => (
              // Show "Create new" in dropdown, but keep original option as value
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props}>{option}</li>
            )}
            getOptionLabel={(option) => {
              // Remove "Create new" prefix from input field display only
              if (option?.startsWith('Create new ')) {
                return option.replace(/^Create new "|"$/g, '');
              }
              return option || '';
            }}
            filterOptions={(options, params) => {
              const filtered = options.filter((option) =>
                option.toLowerCase().includes(params.inputValue.toLowerCase())
              );

              const { inputValue } = params;
              // Suggest the creation of a new value if no matches
              const isExisting = options.some((option) => inputValue === option);
              if (inputValue !== '' && !isExisting) {
                filtered.push(`Create new "${inputValue}"`);
              }

              return filtered;
            }}
            className={globalClasses.notchedOutline}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                label="Tag name"
              />
            )}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: theme.palette.primary.dark,
                  '& .MuiAutocomplete-option': {
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  },
                },
              },
            }}
          />
          {tagExists && (
            <IconButton
              aria-label="Edit tag name"
              onClick={() => {
                setIsEditingTagName(true);
                setNewTagName(selectedTag);
              }}
              size="small"
            >
              <EditIcon color="success" />
            </IconButton>
          )}
        </Box>
      )}

      {/* shade for better UI to show only name textfield while changing group name */}
      {isEditingTagName && <Box className={classes.disabledShade} />}
    </Box>
  );
}
export default TagNameForms;
